<template>
  <div>
    <div
      class="md-layout mobile-compact"
      style="align-items: center;"
    >
      <div class="filters-top-line">
        <md-checkbox
          v-model="hideNotEnabled"
          class="top-padded-item"
          @change="filterByEnabled"
        >
          Hide not enabled
        </md-checkbox>

        <div class="md-layout-item">
          <md-field>
            <md-input
              v-model="search"
              placeholder="Search by everything..."
              @input="searchOnTable"
            />
          </md-field>
        </div>
        <div class="md-layout-item md-size-40 md-small-size-100">
          <grouped-auto-complete
            :groups="groupedApps"
            label="Apps"
            :show-selected-bubbles="true"
            @list-updated="updateAppsFilter"
          />
        </div>
        <!-- Advanced filters -->
        <md-switch
          v-model="showAdvancedFilters"
          class="top-padded-item"
          @change="toggleAdvancedFilters"
        >
          Advanced filters
        </md-switch>
      </div>

      <div
        v-show="showAdvancedFilters"
        class="advanced-filter-container"
      >
        <country-selector-with-continents
          :hide-mode="true"
          :show-label="true"
          :show-grouped-bubbles="true"
          @countryListChanged="updateCountryFilter"
        />
        <div>
          <md-field>
            <label>Platforms</label>
            <md-select
              multiple
              @md-selected="updatePlatformFilter"
            >
              <md-option value="iOS">
                iOS
              </md-option>
              <md-option value="macos">
                macOS
              </md-option>
              <md-option value="android">
                Android
              </md-option>
              <md-option value="windows">
                Windows
              </md-option>
              <md-option value="web">
                Web
              </md-option>
            </md-select>
          </md-field>
        </div>
        <div>
          <md-field>
            <label>Layout style</label>
            <md-select @md-selected="updateLayoutFilter">
              <md-option value="All">
                All
              </md-option>
              <md-option value="small">
                Small
              </md-option>
              <md-option value="medium">
                Medium
              </md-option>
              <md-option value="large">
                Large
              </md-option>
            </md-select>
          </md-field>
        </div>
        <div>
          <md-field>
            <label>Type</label>
            <md-select @md-selected="updateTypeFilter">
              <md-option value="All">
                All
              </md-option>
              <md-option value="General">
                General
              </md-option>
              <md-option value="Detailed">
                Detailed
              </md-option>
            </md-select>
          </md-field>
        </div>
        <div>
          <md-checkbox
            v-model="showOnlySensitive"
            @change="updateSensitiveFilter"
          >
            Only sensitive
          </md-checkbox>
        </div>
      </div>
    </div>
    <!-- table -->
    <md-progress-bar
      v-if="loading"
      md-mode="query"
    />
    <v-client-table
      v-else
      ref="table"
      :data="shortcuts"
      :columns="tableColumns"
      :options="tableOptions"
      class="shortcut-table"
    >
      <template #enabled="props">
        <loadable-checkbox
          :is-checked="props.row.enabled"
          :is-loading="toggleDialog.pending.enabled.includes(props.row.id)"
          :padded="props.row.isVariant"
          @change="showToggleShortcutDialog(props.row, 'enabled', false)"
        />
      </template>

      <template #title="props">
        {{ props.row.title ? props.row.title.en : "" }}
      </template>

      <template #premium="props">
        <loadable-checkbox
          :is-checked="props.row.premium"
          :is-loading="toggleDialog.pending.premium.includes(props.row.id)"
        />
        <!-- The following should be the prop of the <loadable-checkbox> above -->
        <!--               @change="showToggleShortcutDialog(props.row, 'premium')"-->
      </template>

      <template #sensitive="props">
        <loadable-checkbox
          :is-checked="props.row.sensitive"
          :is-loading="toggleDialog.pending.sensitive.includes(props.row.id)"
        />
        <!-- The following should be the prop of the <loadable-checkbox> above -->
        <!--               @change="showToggleShortcutDialog(props.row, 'sensitive')"-->
      </template>

      <template #server_bundles="props">
        <div
          class="server-bundles-container"
          @click="onBundlesClick(props.row)"
        >
          <md-chip
            v-for="sb in props.row.server_bundles"
            :key="sb.id"
            class="md-primary server-bundle-chip"
            md-static
            @click="onBundlesClick(props.row)"
          >
            {{ sb.name }}
          </md-chip>
        </div>
      </template>

      <template #platform_policy="props">
        {{ props.row.platform_access_type }}
      </template>

      <template #comment="props">
        <div
          v-if="props.row.comment"
          class="comment-cell-wrapper"
        >
          <md-button
            v-if="!toggledComments.includes(props.row.id)"
            class="md-dense toggle-comment-button"
            :md-ripple="false"
            @click="toggleComment(props.row.id)"
          >
            Show
          </md-button>
          <p
            v-else
            class="comment-text-para"
            @click="toggleComment(props.row.id)"
          >
            {{ props.row.comment }}
          </p>
        </div>
      </template>

      <template #edit="props">
        <div class="edit-icons">
          <a
            class="interactable"
            :href="'/shortcut/edit/' + props.row.id"
          >
            <md-icon class="gen-key">create</md-icon>
          </a>
          <md-icon
            class="gen-key interactable"
            @click.native="openDetailsDialog(props.row)"
          >
            info
          </md-icon>
        </div>
      </template>

      <template #variants="props">
        <div v-if="!props.row.isVariant">
          <a
            class="interactable"
            :href="`/shortcut/variant/${props.row.id}`"
          >
            <md-icon>add_box</md-icon>
          </a>
          <md-icon
            v-if="props.row.variants"
            class="interactable"
            @click.native="toggleVariants(props.row.id)"
          >
            {{ props.row.isChildRowOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </md-icon>
        </div>
      </template>
    </v-client-table>
    <!--dialogs-->
    <md-dialog :md-active.sync="showDetailsDialog">
      <md-dialog-title>Node configuration</md-dialog-title>
      <md-dialog-actions>
        <md-button
          class="md-primary"
          @click="showDetailsDialog = false"
        >
          Close
        </md-button>
      </md-dialog-actions>

      <md-dialog-content>
        <pre>{{ JSON.stringify(shortcutDetails, null, 4) }}</pre>

        <md-button
          class="md-primary"
          @click="showDetailsDialog = false"
        >
          Save
        </md-button>
      </md-dialog-content>
    </md-dialog>

    <md-dialog
      :md-active.sync="toggleDialog.isActive"
      class="dialog-wrapper"
    >
      <md-dialog-title>{{ toggleDialog.title }}</md-dialog-title>

      <div
        style="overflow: scroll; max-width: 100%;"
        v-html="toggleDialog.diff"
      />

      <md-dialog-actions>
        <md-button
          class="md-primary"
          :disabled="toggleDialog.loading"
          @click="toggleDialog.onConfirm"
        >
          <md-progress-spinner
            v-if="toggleDialog.loading"
            :md-diameter="20"
            :md-stroke="3"
            md-mode="indeterminate"
          />
          <span v-else>Confirm</span>
        </md-button>
        <md-button @click="toggleDialog.isActive = false;">
          Cancel
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <server-bundle-dialog
      :show-bundles-dialog.sync="showBundlesDialog"
      :node="bundleEditShortcut"
      :on-close="onBundleDialogClose"
      is-shortcut-mode
    />
  </div>
</template>

<script>
    import { Event } from "vue-tables-2-premium";
    import { diff, formatters } from "jsondiffpatch";

    import CountrySelectorWithContinents from "../Forms/Fields/CountrySelectorWithContinents";
    import LoadableCheckbox from "@/components/Forms/Fields/LoadableCheckbox";
    import GroupedAutoComplete from "@/components/Forms/Fields/GroupedAutoComplete";
    import ServerBundleDialog from "../Forms/nodes/ServerBundleDialog";

    // import shortcuts from "@/../mocks/shortcuts";

    export default {
        name: "ShortcutsTable",
        components: {
            CountrySelectorWithContinents,
            LoadableCheckbox,
            GroupedAutoComplete,
            ServerBundleDialog
        },
        props: {
            tableHeaderColor: {
                type: String,
                default: ""
            }
        },
        data() {
            return {
                search: "",
                hideNotEnabled: false,
                showOnlySensitive: false,
                loading: true,
                total: 0,
                selected: [],
                shortcuts: [],
                sourceShortcuts: [],
                componentKey: 0,
                showDetailsDialog: false,
                shortcutDetails: {},

                showAdvancedFilters: false,

                tableColumns: [
                    "enabled", "title", "slug", "premium", "sensitive", "layout_style",
                    "index", "server_bundles", "comment", "edit", "variants"
                ],
                tableOptions: {
                    perPage: 100,
                    pagination: {
                        show: true,
                    },
                    showChildRowToggler: false,
                    filterable: true, // might not be needed
                    sortable: ["enabled", "premium", "sensitive", "type", "index", "platform_policy", "comment"],
                    rowClassCallback: function(row) {
                        if (row.isVariant) {
                            return `child-row-${this.rowEvenIterator ? "even" : "odd"}`;
                        } else {
                            this.rowEvenIterator = !this.rowEvenIterator;
                            return `parent-row-${this.rowEvenIterator ? "even" : "odd"}`;
                        }
                    },
                    customFilters: [
                        {
                            name: "country",
                            callback(row, query) {
                                if (query.length === 0) {
                                    return true;
                                }
                                return query.some(p =>
                                    row.countries_access_type === "common"
                                    || (row.countries_access_type === "blacklist" && !row.countries_iso_codes.includes(p))
                                    || (row.countries_access_type === "whitelist" && row.countries_iso_codes.includes(p))
                                );
                            }
                        },
                        {
                            name: "platform",
                            callback(row, query) {
                                if (query.length === 0) {
                                    return true;
                                }
                                return query.some(p =>
                                    row.platform_access_type === "common"
                                    || (row.platform_access_type === "blacklist" && !row.platforms.includes(p))
                                    || (row.platform_access_type === "whitelist" && row.platforms.includes(p))
                                );
                            }
                        },
                        {
                            name: "type",
                            callback(row, query) {
                                if (query === "All") {
                                    return true;
                                }
                                return row.type === query;
                            }
                        },
                        {
                            name: "apps",
                            callback(row, query) {
                                if (query.length === 0) {
                                    return true;
                                }
                                return query.some(p =>
                                    row.apps_access_type === "common"
                                    || (row.apps_access_type === "blacklist" && !row.apps.some(app => app.app_identity === p))
                                    || (row.apps_access_type === "whitelist" && row.apps.some(app => app.app_identity === p))
                                );
                            }
                        },
                        {
                            name: "enabled",
                            callback(row, hideNotEnabled) {
                                if (hideNotEnabled) {
                                    return row.enabled;
                                }
                                return true;
                            }
                        },
                        {
                            name: "text-search",
                            callback(row, keywords) {
                                if (keywords.length === 0) {
                                    return true;
                                }
                                const shortcut = JSON.stringify(row).toLowerCase();
                                return keywords.every(keyword => shortcut.includes(keyword));
                            }
                        },
                        {
                            name: "layout",
                            callback(row, layoutStyle) {
                                if (layoutStyle === "All") {
                                    return true;
                                }
                                return row.layout_style === layoutStyle;
                            }
                        },
                        {
                            name: "sensitive",
                            callback(row, isOnlySensitive) {
                                if (!isOnlySensitive) {
                                    return true;
                                }
                                return row.sensitive;
                            }
                        },
                    ]
                },
                rowEvenIterator: false,

                toggleDialog: {
                    isActive: false,
                    title: "",
                    onConfirm: () => {},
                    diff: "",
                    loading: false,
                    pending: {
                        enabled: [],
                        premium: [],
                        sensitive: [],
                    }
                },

                toggledComments: [],

                tagList: [],
                appList: [],

                enabledPending: [],
                premiumPending: [],

                groupedApps: {},

                showBundlesDialog: false,
                bundleEditShortcut: null,
            };
        },
        mounted() {
            this.getApps();
            this.getShortcuts();
        },
        methods: {
            searchOnTable() {
                this.unfoldAllShortcuts();
                const searchItems = this.search.toLowerCase().split(" ");
                Event.$emit("vue-tables.filter::text-search", searchItems);
            },
            filterByEnabled() {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::enabled", this.hideNotEnabled);
            },
            toggleAdvancedFilters() {
                if (!this.showAdvancedFilters) {
                    // clearing advanced filters when they're hidden
                    Event.$emit("vue-tables.filter::country", []);
                    Event.$emit("vue-tables.filter::platform", []);
                    Event.$emit("vue-tables.filter::type", "All");
                    Event.$emit("vue-tables.filter::layout", "All");
                    Event.$emit("vue-tables.filter::type", "All");
                    Event.$emit("vue-tables.filter::sensitive", false);
                }
            },
            updateCountryFilter(value) {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::country", value);
            },
            updatePlatformFilter(value) {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::platform", value);
            },
            updateTypeFilter(value) {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::type", value);
            },
            updateAppsFilter(value) {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::apps", value);
            },
            updateLayoutFilter(value) {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::layout", value);
            },
            updateSensitiveFilter() {
                this.unfoldAllShortcuts();
                Event.$emit("vue-tables.filter::sensitive", this.showOnlySensitive);
            },
            showToggleShortcutDialog (targetShortcut, toggledKey, isMentionKeyInTitle = true) {
                const shortcut = {...targetShortcut};
                shortcut[toggledKey] = !shortcut[toggledKey];

                const originalShortcut = this.sourceShortcuts.find(s => s.id === shortcut.id);

                const d = diff(originalShortcut, shortcut);
                this.toggleDialog.diff = formatters.html.format(d, originalShortcut);
                this.toggleDialog.title = `${shortcut[toggledKey] ? "Enable" : "Disable"} ${isMentionKeyInTitle ? toggledKey + " for " : ""}${shortcut.slug}?`;
                this.toggleDialog.onConfirm = () => {
                    this.toggleDialog.loading = true;
                    this.toggleDialog.pending[toggledKey] = [shortcut.id];

                    this.$http.post("/v1/shortcut/update", shortcut)
                        .then(() => {
                            this.sourceShortcuts.find(s => s.slug === shortcut.slug)[toggledKey] = shortcut[toggledKey];
                            this.shortcuts.find(s => s.slug === shortcut.slug)[toggledKey] = shortcut[toggledKey];

                            this.toggleDialog.isActive = false;

                            this.toggleDialog.pending[toggledKey] = "";
                        })
                        .catch(err => console.error(err))
                        .finally(() => { this.toggleDialog.loading = false; });
                };
                this.toggleDialog.isActive = true;
            },
            toggleVariants(rowId) {
                const index = this.shortcuts.findIndex(s => s.id === rowId);
                const variants = this.shortcuts[index].variants;

                if (this.shortcuts[index].isChildRowOpen) {
                    this.shortcuts.splice(index + 1, variants.length);
                    this.shortcuts[index].isChildRowOpen = false;
                } else {
                    this.shortcuts.splice(index + 1, 0, ...variants);
                    this.shortcuts[index].isChildRowOpen = true;
                }
            },
            toggleComment(rowId) {
                const index = this.toggledComments.findIndex(i => i === rowId);
                if (index !== -1) {
                    this.toggledComments.splice(index, 1);
                } else {
                    this.toggledComments.push(rowId);
                }
            },
            getApps() {
                this.$http.get("/v1/admin/store/apps").then(res => {
                    const appList = res.data.apps.map(a => a.identity);

                    const groupedApps = {};
                    groupedApps.assguard = appList.filter(app => /assguard/.test(app));
                    groupedApps.clear = appList.filter(app => /clearvpn/.test(app));

                    this.groupedApps = groupedApps;
                });
            },
            getShortcuts() {
                this.$http.get("/v1/shortcuts").then(result => {
                // Promise.resolve({ data: shortcuts}).then(result => {
                    const shortcuts = result.data.shortcuts.filter(s => !s.slug.includes("_"));
                    result.data.shortcuts.filter(s => s.slug.includes("_")).map(s => {
                        const [slug,] = s.slug.split("_");

                        const generalShortcut = shortcuts.find(s => s.slug === slug);
                        if (!generalShortcut) {
                            shortcuts.push(s);
                            return;
                        }

                        s.isVariant = true;
                        if (generalShortcut.variants) {
                            generalShortcut.variants.push(s);
                        } else {
                            generalShortcut.variants = [s];
                        }
                    });

                    this.sourceShortcuts = shortcuts;
                    this.shortcuts = shortcuts;
                    this.loading = false;
                }, err => {
                    console.error(err);
                });
            },
            openDetailsDialog(item) {
                this.showDetailsDialog = true;

                this.$http.get("/v1/shortcut/id/" + item.id).then(
                    res => {
                        this.shortcutDetails = res.data.shortcut;
                    }
                );

            },
            unfoldAllShortcuts() {
                this.shortcuts.forEach(s => {
                    if (s.variants && !s.isChildRowOpen) {
                        this.toggleVariants(s.id);
                    }
                });
            },
            onBundlesClick(shortcut) {
                this.showBundlesDialog = true;
                this.bundleEditShortcut = shortcut;
            },
            onBundleDialogClose(shortcutId, newServerBundles) {
                this.showBundlesDialog = false;

                if (shortcutId && newServerBundles) {
                    this.shortcuts.find(s => s.id === shortcutId).server_bundles = newServerBundles;
                    this.sourceShortcuts.find(s => s.id === shortcutId).server_bundles = newServerBundles;
                }
            }
        },

    };
</script>

<style lang="scss">
    .filters-top-line {
        width: 100%;
        display: flex;
        align-items: flex-start;

        .top-padded-item {
            margin-top: 32px;
        }
    }

    @media only screen and (max-width: 450px) {
      .filters-top-line {
        flex-wrap: wrap;
      }
    }

    .advanced-filter-container {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fit, min(400px));
        grid-column-gap: 25px;
    }

    .node-details {
        padding: 50px 100px;
    }

    .shortcut-table {
        margin-top: 50px;
        width: 100%;
        overflow-x: scroll;
    }

    .child-chip-container {
        display: inline-block;
        width: 120px;
    }
    .VueTables__search-field {
        display: none;
    }
    .VueTables__limit {
        display: none;
    }
    .VueTables__limit-field {
        display: none;
    }
    .VueTables__table {
        width: 100%;
        min-width: 850px;
        overflow-wrap: break-word;
    }

    .table {
        border: none;
        border-collapse: collapse;
    }

    .parent-row-even {
        background-color: #e2e2e2;
    }
    .child-row-even {
        background-color: #e2e2e2;
    }


    .server-bundles-container {
        text-align: center;
        cursor: pointer;
    }

    // might mess up some material selects
    .md-list-item-text {
        align-items: center !important;
        justify-content: center;
    }

    .table-checkbox {
        display: block !important;
        margin: 16px 0 16px 8px !important;
    }
    .table-checkbox.child-row-enabled {
        margin: 16px 0 16px 20px !important;
    }

    .comment-cell-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;

        .toggle-comment-button {
            min-width: 60px !important;
            width: 60px;
        }

        .comment-text-para {
            cursor: pointer;
        }
    }

    .server-bundle-chip {
        font-size: 10px !important;
        padding: 0 5px !important;
        line-height: 24px !important;
        height: 24px !important;
        background-color: #b4bbe4 !important;
        color: black !important;
        cursor: pointer !important;
    }

    .edit-icons {
        display: flex;
        flex-direction: column;
        margin: 0 5px 0 0;
        width: 25px;

        a {
          width: 25px;
          margin-bottom: 15px;
        }

        & > i {
          margin: 0 !important;
        }
    }

    .mobile-compact {
        padding: 0 !important;
    }

    .interactable {
        cursor: pointer !important;
    }

    .dialog-wrapper .md-dialog-container {
      max-width: 100%;
    }

    .VuePagination__pagination {
      margin-top: 50px;
      padding: 0;
      list-style-type: none;
      display: flex;
      justify-content: center;

      li.VuePagination__pagination-item {
        padding: 0 6px;
        a.page-link {
            font-size: 15px;
            padding: 2px 8px;
            cursor: pointer;

            &:focus, &:hover {
              color: white !important;
              background-color: #00b0ff;
            }
        }
      }
    }

    @media only screen
    and (max-width: 850px) {
        .mobile-compact {
            .md-layout-item {
                padding: 0 5px;
            }

            .edit-icons {
                flex-direction: column;

                i {
                    margin-top: 8px;
                }
            }
        }
    }
    //noinspection CssInvalidAtRule
    @-webkit-media only screen and (max-width: 850px) {
        .mobile-compact {
            .md-layout-item {
                padding: 0 5px;
            }

            .edit-icons {
                flex-direction: column;

                i {
                    margin-top: 8px;
                }
            }
        }
    }
</style>
